import React from 'react';
import clsx from 'clsx';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import styles from './MandateChecksGate.module.scss';
import { MandateChecksResult } from '../../debtProductTypes';
import { CHECKS_NOT_VERIFIED_MESSAGE, CHECKS_STATUS_NOT_VERIFIED, CHECKS_TOOLTIP_HIDE_DELAY_MS } from '../../DebtProductsConstants';
import { QUALIFICATION_STATUS } from '../../../../utils/constants/commonConstants';
import { errorIconRed, greenCheck, infoIconGray, infoIconGreen, infoIconRed } from '../../../../assets/hostedassets';
import { isChecksVerdictDegraded } from '../../DebtProductsUtil';

interface MandateChecksGateProps {
    checksData?: MandateChecksResult;
}

const PILL_STATE = {
    passed: { label: QUALIFICATION_STATUS.QUALIFIED as string, icon: infoIconGreen, className: styles.PillPassed },
    refused: { label: QUALIFICATION_STATUS.UNQUALIFIED as string, icon: infoIconRed, className: styles.PillRefused },
    degraded: { label: CHECKS_STATUS_NOT_VERIFIED, icon: infoIconGray, className: styles.PillNeutral },
};

/**
 * The mandate-checks gate: a status pill, the rule-by-rule tooltip behind it, and the divider
 * separating it from the action button.
 *
 * Rendered for both the qualification engine and the create-time checks engine — they emit the
 * same { result, rules[] } shape, so one component keeps the two gates from drifting apart.
 */
const MandateChecksGate = ({ checksData }: MandateChecksGateProps) => {
    // Mutually exclusive by construction: isChecksVerdictDegraded returns false for a refusal,
    // because refusing IS a decision — it shows its rule rows rather than "couldn't check".
    const degraded = isChecksVerdictDegraded(checksData);
    const refused = checksData?.result === false;

    const state = degraded ? PILL_STATE.degraded : refused ? PILL_STATE.refused : PILL_STATE.passed;
    const hasDetail = degraded || (checksData?.rules?.length ?? 0) > 0;

    const pill = (
        <div className={clsx(styles.Pill, state.className, { [styles.PillClickable]: hasDetail })}>
            <span>{state.label}</span>
            <img src={state.icon} alt={state.label} className={styles.PillIcon} />
        </div>
    );

    if (!hasDetail) {
        return (
            <>
                {pill}
                <div className={styles.DividerWide} />
            </>
        );
    }

    // On a degraded verdict the rule rows carry their own affirmative text, which would assert
    // the very thing that could not be checked. Explain instead.
    const tooltipContent = degraded ? (
        CHECKS_NOT_VERIFIED_MESSAGE
    ) : (
        <div className={styles.TooltipBody}>
            {checksData?.rules?.map((rule, index) => (
                <div key={index} className={styles.RuleRow}>
                    <div className={styles.RuleIconWrap}>
                        <img
                            src={rule.result === true ? greenCheck : errorIconRed}
                            alt={rule.result === true ? QUALIFICATION_STATUS.QUALIFIED : QUALIFICATION_STATUS.UNQUALIFIED}
                            className={styles.RuleIcon}
                        />
                    </div>
                    <span className={styles.RuleText}>{rule.message}</span>
                </div>
            ))}
        </div>
    );

    return (
        <>
            <div className={styles.Anchor}>
                <Tooltip
                    content={tooltipContent}
                    direction="left"
                    fitContent={true}
                    tipClassName={styles.TooltipTip}
                    contentClassName={styles.TooltipContent}
                    hideDelay={CHECKS_TOOLTIP_HIDE_DELAY_MS}
                >
                    {pill}
                </Tooltip>
            </div>
            <div className={styles.Divider} />
        </>
    );
};

export default MandateChecksGate;
